import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Show } from '@/components';
import { WelletContext } from '@/context/wellet/welletContext';
import { formatPaymentMethod } from '@/helpers/format';
import { formatDateTranslation } from '@/helpers/dates';
import { useDownloadPdfMutationNew } from '@/hooks/mutations/useDownloadPdfMutationNew';
import { getPaymentsMethods, getWithdrawalMethodHistory } from '@/services';
import { CardForm, Loading, PageError } from '@/uiComponents/common';
import SelectForm from '@/uiComponents/common/selects/selectNew';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import { ArrowRight, History } from 'lucide-react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

const CommissionCard = ({
  t,
  className,
  mode,
  showInfo,
  imageUrl,
  id,
  imgLoading,
  rolText,
}) => {
  const welletContext = useContext(WelletContext);
  const { t: translate } = useTranslation('translation', { keyPrefix: 'forms.userForm' });
  const { t: tHistory } = useTranslation('translation', {
    keyPrefix: 'screens.profile.withdrawalHistory',
  });
  const { t: translatePayment } = useTranslation();
  const language = useSelector((state) => state.app.language);
  const [historyOpen, setHistoryOpen] = useState(false);

  const paymentsQuery = useQuery({
    queryKey: ['payments-methods'],
    queryFn: () => getPaymentsMethods(welletContext),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: mode !== 'PROFILE',
  });

  const historyQuery = useQuery({
    queryKey: ['withdrawal-method-history', id],
    queryFn: () => getWithdrawalMethodHistory(welletContext, id),
    enabled: !!id && historyOpen,
    ...defaultQueryOptions,
  });
  const history = historyQuery?.data?.data ?? [];

  const downloadPdfMutation = useDownloadPdfMutationNew(); // Instancia la mutación

  const printPDF = () => {
    downloadPdfMutation.mutate(id);
  };

  const renderMethodBadge = (method) => {
    if (!method) return <span className='tw-text-muted-foreground'>—</span>;
    return (
      <Badge variant='outline' className='tw-px-2.5 tw-py-1'>
        {formatPaymentMethod(method, translatePayment)}
      </Badge>
    );
  };

  return (
    <>
      <CardForm
        title={t('comissionCard.title')}
        subtitle={t('comissionCard.subtitle')}
        className={className}
      >
        <div className='row tw-mb-6'>
          {showInfo && mode !== 'PROFILE' && (
            <div className='col-12 tw-mb-6'>
              <div className='d-flex tw-items-center tw-justify-between !tw-mb-2'>
                <Label className='!tw-mb-0'>{t('paymentMethod.title')}</Label>
                {id ? (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='tw-h-7 tw-px-2 tw-text-xs'
                    onClick={() => setHistoryOpen(true)}
                  >
                    <History className='tw-h-3.5 tw-w-3.5 tw-mr-1' />
                    {tHistory('trigger')}
                  </Button>
                ) : null}
              </div>
              <SelectForm
                name={'withdrawalMethod'}
                label={null}
                placeholder={t('paymentMethod.placeholder')}
                options={paymentsQuery?.data?.data?.map((p) => ({
                  label: formatPaymentMethod(p, translatePayment),
                  value: p,
                }))}
              />
            </div>
          )}
          {mode === 'CONCIERGE' && (
            <>
              {/* <div className='col-12 tw-mb-6'>
                <Label className='!tw-mb-3'>{t('rowCommissions.title')}</Label>
                <InputCheckboxForm
                  name={'isCommissionDisabled'}
                  label={translate('inputModernSwithcer.label')}
                />
              </div>

              <div className='col-12 tw-mb-6'>
                <InputNumberForm
                  name={'commissionPlusPercentage'}
                  className='mb-3'
                  currencySymbol={'%'}
                  label={translate('inputComissionsPercentage.label')}
                  maxNumber={100}
                  minNumber={0}
                  isDisabled={isCommissionDisabled}
                  placeholder={translate('inputComissionsPercentage.placeholder')}
                />
              </div> */}

              <div className='col-12'>
                <Label className='!tw-mb-3'>
                  {translate('linkQr.title', {
                    name: rolText,
                  })}
                </Label>
                {imageUrl && !imgLoading ? (
                  <div>
                    <img alt='' src={imageUrl} width='250px' height='250px' />
                    <Button
                      className={'mt-2 d-block'}
                      onClick={printPDF}
                      size={'sm'}
                      disabled={downloadPdfMutation.isLoading}
                      loading={downloadPdfMutation.isLoading}
                      type='button'
                    >
                      {translate('linkQr.download')}
                    </Button>
                  </div>
                ) : (
                  <p>{translate('linkQr.noLink')}</p>
                )}
              </div>
            </>
          )}
        </div>
      </CardForm>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className='!tw-max-w-3xl'>
          <DialogHeader>
            <DialogTitle>{tHistory('title')}</DialogTitle>
            <DialogDescription>{tHistory('subtitle')}</DialogDescription>
          </DialogHeader>

          <Show>
            <Show.When isTrue={historyQuery.isLoading || historyQuery.isFetching}>
              <div className='tw-py-6'>
                <Loading global={false} />
              </div>
            </Show.When>
            <Show.When isTrue={historyQuery.isError}>
              <PageError />
            </Show.When>
            <Show.When isTrue={history.length === 0}>
              <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-muted-foreground'>
                <History className='tw-h-8 tw-w-8 tw-mb-2 tw-text-gray-400' />
                <p className='tw-text-sm'>{tHistory('empty')}</p>
              </div>
            </Show.When>
            <Show.Else>
              <div className='tw-max-h-[60vh] tw-overflow-auto'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{tHistory('columns.date')}</TableHead>
                      <TableHead>{tHistory('columns.modifiedBy')}</TableHead>
                      <TableHead>{tHistory('columns.change')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((item) => {
                      const { dateTime } = formatDateTranslation(
                        new Date(item.modifiedDate),
                        language,
                        'small',
                      );
                      return (
                        <TableRow key={item.id}>
                          <TableCell className='sentences-capitalize tw-whitespace-nowrap'>
                            {dateTime}
                          </TableCell>
                          <TableCell>
                            {item.modifiedNameBy || (
                              <span className='tw-text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className='tw-flex tw-items-center tw-gap-2 tw-flex-wrap'>
                              {renderMethodBadge(item.fromMethod)}
                              <ArrowRight className='tw-h-4 tw-w-4 tw-text-muted-foreground' />
                              {renderMethodBadge(item.toMethod)}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Show.Else>
          </Show>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CommissionCard;
